// components/secure/FloridaDncPanel.js
// Contact Compliance → Florida Do Not Call list.
//
// FDACS sells the Florida list (statewide or per area code) and publishes it quarterly;
// there is no automated download, so the managing broker uploads the file here each
// quarter. Staff see what is loaded, which quarter, and whether it has gone stale
// ("Florida list is older than one quarter" — calls and texts to those numbers are
// refused until the current quarter is loaded). Backend: routes/floridaDnc.js.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

const input = 'px-3 py-2 border border-gray-300 rounded-md text-sm';
const btn = 'px-3 py-1.5 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300';
const TONE = {
    current: 'bg-green-100 text-green-800', ageing: 'bg-amber-100 text-amber-800',
    'never loaded': 'bg-slate-100 text-slate-600', 'stale — calls blocked': 'bg-red-100 text-red-800',
};
const when = (d) => (d ? new Date(d).toLocaleString() : '—');
const hh = (n) => `${String(n).padStart(2, '0')}:00`;

export default function FloridaDncPanel({ onChanged }) {
    const api = useApiClient();
    const me = getCurrentUser();
    const broker = !!me?.isManagingBroker;
    const [s, setS] = useState(null);
    const [runs, setRuns] = useState([]);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [showRuns, setShowRuns] = useState(false);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ quarter: '', scope: 'statewide', area_codes: '', file: null });

    const load = useCallback(async () => {
        try {
            const [st, r] = await Promise.all([
                api.get('/api/contact-compliance/florida/status'),
                api.get('/api/contact-compliance/florida/runs'),
            ]);
            setS(st); setRuns(r || []);
            setForm((f) => (f.quarter ? f : { ...f, quarter: st.current_quarter.key }));
            if (st.site === 'FL' || st.warnings.length) setOpen(true);
        } catch (e) { if (e.status !== 403) setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const send = async () => {
        setBusy(true); setError(''); setNotice('');
        try {
            const fd = new FormData();
            fd.append('quarter', form.quarter);
            fd.append('scope', form.scope);
            if (form.scope === 'area_codes') fd.append('area_codes', form.area_codes);
            fd.append('file', form.file);
            const r = await api.post('/api/contact-compliance/florida/upload', fd, true);
            setNotice(r.message);
            setForm({ ...form, file: null, area_codes: '' });
            if (onChanged) onChanged();
        } catch (e) {
            setError(e.payload?.message || e.message);
        } finally { setBusy(false); await load(); }
    };

    if (!s) return error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null;
    const loadedCodes = s.area_codes.filter((a) => a.state !== 'never loaded');
    const red = s.stale > 0;

    return (
        <div className={`bg-white border rounded-lg p-4 mb-4 space-y-3 ${red ? 'border-red-300' : 'border-slate-200'}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="font-semibold text-slate-800">Florida Do Not Call list</h2>
                    <p className="text-xs text-slate-500">
                        Checked before any prospecting call or text to a Florida number, a contact with a Florida address,
                        or anyone when this is the Florida site. FDACS publishes it quarterly; current quarter is {s.current_quarter.label}.
                    </p>
                </div>
                <button className="text-sm text-indigo-700 hover:underline whitespace-nowrap" onClick={() => setOpen((v) => !v)}>
                    {open ? 'Hide' : 'Show'}
                </button>
            </div>

            {s.warnings.map((w) => (
                <p key={w} className={`text-sm rounded px-3 py-2 ${/older than one quarter/.test(w) ? 'bg-red-50 text-red-800 font-medium' : 'bg-amber-50 text-amber-900'}`}>{w}</p>
            ))}
            {error && <p className="text-sm text-red-600">{error}</p>}
            {notice && <p className="text-sm text-green-700">{notice}</p>}

            {open && (
                <>
                    <div className="grid md:grid-cols-3 gap-3 text-sm">
                        <div className="rounded border border-slate-200 p-3">
                            <div className="font-medium">Loaded</div>
                            <div>{loadedCodes.length ? `${loadedCodes.length} area code(s)` : 'Nothing yet'}{s.statewide ? ` · statewide ${s.statewide.quarter}` : ''}</div>
                        </div>
                        <div className="rounded border border-slate-200 p-3">
                            <div className="font-medium">Florida hours</div>
                            <div>{hh(s.rules.calling_hours.start)}–{hh(s.rules.calling_hours.end)} in the number's local time;
                                at most {s.rules.max_calls_per_24h?.calls} solicitation calls per person per 24 hours on one subject.</div>
                            <div className="text-xs text-slate-500 mt-0.5">{s.rules.calling_hours.authority}; {s.rules.max_calls_per_24h?.authority}</div>
                        </div>
                        <div className="rounded border border-slate-200 p-3">
                            <div className="font-medium">Last load</div>
                            {s.last_run
                                ? <div>{when(s.last_run.started_at)} · <strong>{s.last_run.outcome}</strong><div className="text-xs text-slate-600 mt-0.5">{s.last_run.message}</div></div>
                                : <div>Never loaded.</div>}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                        {s.area_codes.map((a) => (
                            <span key={a.area_code} className={`px-2 py-0.5 rounded text-xs ${TONE[a.state] || ''}`}
                                title={a.quarter ? `${a.quarter} · ends ${a.period_end} · ${a.state}` : 'not loaded — prospecting calls refused'}>
                                {a.area_code}{a.quarter ? ` · ${a.quarter}` : ''}
                            </span>
                        ))}
                    </div>

                    {broker && (
                        <div className="border-t border-slate-200 pt-3">
                            <h3 className="text-sm font-semibold text-slate-700">Load this quarter's file</h3>
                            <p className="text-xs text-slate-500 mb-2">
                                Buy and download it from FDACS (ConsumerCompliance.FDACS.gov — statewide or by area code; the next quarter is
                                out about 30 days before it starts). Upload the text file as downloaded (unzip it first). It replaces what is held
                                for the area codes it covers; an older quarter never replaces a newer one.
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <select className={input} value={form.quarter} onChange={(e) => setForm({ ...form, quarter: e.target.value })}>
                                    <option value={s.current_quarter.key}>{s.current_quarter.label} (current)</option>
                                    <option value={s.next_quarter.key}>{s.next_quarter.label} (next)</option>
                                </select>
                                <select className={input} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                                    <option value="statewide">Statewide list</option>
                                    <option value="area_codes">Specific area codes</option>
                                </select>
                                {form.scope === 'area_codes' && (
                                    <input className={`${input} w-56`} placeholder="Area codes, e.g. 305, 786" value={form.area_codes}
                                        onChange={(e) => setForm({ ...form, area_codes: e.target.value })} />
                                )}
                                <input type="file" accept=".txt,.csv,.dat,text/plain" onChange={(e) => setForm({ ...form, file: e.target.files[0] || null })} />
                                <button className={btn} disabled={busy || !form.file || (form.scope === 'area_codes' && !form.area_codes.trim())} onClick={send}>
                                    {busy ? 'Loading…' : 'Load'}
                                </button>
                            </div>
                        </div>
                    )}

                    {s.versions.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-slate-700 mb-1">Versions loaded</h3>
                            <div className="text-xs"><DataList rows={s.versions} rowKey="version_id" csvName="florida-dnc-versions" defaultSort={{ key: 'loaded_at', dir: 'desc' }}
                                columns={[
                                    { key: 'quarter', label: 'Quarter', render: (v) => <span className="font-medium">{v.quarter}</span> },
                                    { key: 'scope', label: 'Scope', value: (v) => (v.scope === 'statewide' ? 'Statewide' : (v.area_codes || []).join(', ')) },
                                    { key: 'numbers_loaded', label: 'Numbers', type: 'number', render: (v) => `${Number(v.numbers_loaded).toLocaleString()}${v.duplicates ? ` (${v.duplicates} dup.)` : ''}` },
                                    { key: 'file_name', label: 'File' },
                                    { key: 'loaded_at', label: 'Loaded', type: 'date', render: (v) => <span className="whitespace-nowrap">{when(v.loaded_at)}</span> },
                                ]} /></div>
                        </div>
                    )}

                    <div>
                        <button className="text-sm text-indigo-700 hover:underline" onClick={() => setShowRuns((v) => !v)}>
                            {showRuns ? 'Hide' : 'Show'} load history ({runs.length})
                        </button>
                        {showRuns && (
                            <div className="mt-2 text-xs"><DataList rows={runs} rowKey="run_id" csvName="florida-dnc-runs" defaultSort={{ key: 'started_at', dir: 'desc' }}
                                filters={[{ key: 'outcome', label: 'Outcome' }]}
                                columns={[
                            { key: 'started_at', label: 'Started', type: 'date', render: (r) => <span className="whitespace-nowrap">{when(r.started_at)}</span> },
                            { key: 'quarter_key', label: 'Quarter', render: (r) => r.quarter_key || '' },
                            { key: 'outcome', label: 'Outcome', render: (r) => <span className="font-medium">{r.outcome}</span> },
                            { key: 'numbers_loaded', label: 'Numbers', type: 'number', render: (r) => r.numbers_loaded ?? '' },
                            { key: 'message', label: 'Message' },
                                ]} /></div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
